/**
 * Auth Redirect Singleton
 *
 * Sends a signed-in GM Rewards member off a guarded page and on to their account — an
 * authenticated member visiting `/rewards` belongs on `/myaccount/rewards/dashboard`.
 * Both paths are authored in AEM and arrive as data attributes on a single inert
 * `gmx-auth-redirect` marker element.
 *
 * GBPE is not loaded here.  `gmx-account` in the primary nav injects it, so a page
 * without the nav never gets GBPE and so never redirects.
 */

export const MARKER_SELECTOR = 'gmx-auth-redirect'

export const GBPE_WAIT_MS = 10000

export const GBPE_POLL_MS = 250

/** Production GBPE returns a locale-formatted string ('14,646'); some environments return a number. */
interface GBPERewardsResponse {
	data?: {
		reward?: {
			points?: number | string | null
		}
	}
}

// Matches the balance contract gmx-account already applies to the same payload: either
// plain digits, or three-digit groups separated by a comma or one of the spaces used as
// a separator (including the non-breaking kinds fr-CA uses).
const GROUPED_INTEGER = /^(\d+|\d{1,3}([,\u0020\u00a0\u202f\u2009]\d{3})+)$/
const GROUP_SEPARATORS = /[,\u0020\u00a0\u202f\u2009]/g

// The exported instance is frozen, so every piece of mutable state lives out here.
const hasRedirectedMap = new WeakMap<GMXAuthRedirectSingleton, boolean>()
const gbpeWatchStartedMap = new WeakMap<GMXAuthRedirectSingleton, boolean>()
const authGenerationMap = new WeakMap<GMXAuthRedirectSingleton, number>()

function normalizePath(path: string): string {
	const withoutQuery = path.trim().toLowerCase().split(/[?#]/)[0]
	const withoutOrigin = withoutQuery.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/, '')
	const withoutExtension = withoutOrigin.replace(/\.html$/, '')
	const withoutTrailingSlash = withoutExtension.replace(/\/+$/, '')

	if (!withoutTrailingSlash) {
		return '/'
	}

	return withoutTrailingSlash.startsWith('/') ? withoutTrailingSlash : `/${withoutTrailingSlash}`
}

// `/content/<brand>/<region>/<country>/<lang>`, the root author and preview serve a page
// from. What follows it is the path the page is published under.
const AEM_SITE_ROOT = /^\/content\/[^/]+\/[^/]+\/[^/]+\/[^/]+/

/**
 * Reduces an author or preview URL to the path the page will be published at, so the
 * authored `/rewards` can be compared against it exactly.  Matching on a suffix instead
 * would let `/content/<brand>/na/us/en/myaccount/rewards` answer for `/rewards`.
 */
function toPublishedPath(path: string): string {
	if (!path.startsWith('/content/')) {
		return path
	}

	return path.replace(AEM_SITE_ROOT, '') || '/'
}

function pathMatches(currentPath: string, guardedPath: string): boolean {
	return toPublishedPath(currentPath) === guardedPath
}

/**
 * Membership is a reward carrying a balance.  The number itself is never read, but it is
 * still validated rather than merely counted as present: a `points` of 'Pending' is a
 * contract violation rather than a balance, and honouring it would redirect a visitor
 * the nav is simultaneously showing no balance for.  0 is a real balance, which is why
 * this cannot be a truthiness check.
 */
function hasRewardsBalance(rewards: GBPERewardsResponse | null): boolean {
	const points = rewards?.data?.reward?.points

	if (typeof points === 'number') {
		return Number.isSafeInteger(points) && points >= 0
	}

	if (typeof points !== 'string' || !GROUPED_INTEGER.test(points.trim())) {
		return false
	}

	// Past 2^53 the value is silently rounded, so gmx-account discards it and this has to
	// as well or the two disagree again at the far end of the range.
	return Number.isSafeInteger(Number(points.trim().replace(GROUP_SEPARATORS, '')))
}

/**
 * The authored destination resolved against the page it would be navigated from.  That
 * is the form the loop guard has to compare against — a relative `./rewards` looks
 * unlike `/rewards` as text but is the same page — and the only form worth trusting.
 *
 * Same-origin only.  A `javascript:` destination would otherwise be stored script
 * injection against every signed-in member, and an off-site one is never what an
 * account page means.
 */
function resolveDestination(destination: string): URL | null {
	try {
		const resolved = new URL(destination, window.location.href)

		return resolved.origin === window.location.origin ? resolved : null
	} catch {
		return null
	}
}

export class GMXAuthRedirectSingleton {
	constructor() {
		if (!window.gmxAuthRedirectSingleton) {
			window.gmxAuthRedirectSingleton = this

			// Auth may already have settled, or may settle later; cover both.
			void this.evaluate()
			window.addEventListener('gbpe:authStateUpdated', () => void this.onAuthStateUpdated())
		} else {
			window.console.log('Tried to instantiate auth redirect singleton...already instantiated')
		}
	}

	/**
	 * Moving the generation on is what lets a rewards read already in flight recognise
	 * that the balance it is about to receive belongs to the session before this one.
	 */
	public onAuthStateUpdated(): Promise<void> {
		authGenerationMap.set(this, (authGenerationMap.get(this) ?? 0) + 1)

		return this.evaluate()
	}

	/**
	 * Runs are independent rather than serialized.  Queueing them behind one another
	 * would let a rewards read that never settles block every auth state after it, and
	 * sharing one run would answer a new caller out of the old session.  Acting on the
	 * wrong run is instead prevented after the await, by the generation and
	 * redirect-once checks.
	 */
	public async evaluate(): Promise<void> {
		if (hasRedirectedMap.get(this)) {
			return
		}

		const marker = document.querySelector(MARKER_SELECTOR)

		if (!marker) {
			return
		}

		const guardedPage = marker.getAttribute('data-current-page')
		const destination = marker.getAttribute('data-destination-page')

		if (!guardedPage || !destination) {
			window.console.warn('gmx-auth-redirect is missing data-current-page or data-destination-page; no redirect will run.')

			return
		}

		const currentPath = normalizePath(window.location.pathname)

		if (!pathMatches(currentPath, normalizePath(guardedPage))) {
			return
		}

		const target = resolveDestination(destination)

		if (!target) {
			window.console.warn(`gmx-auth-redirect: destination "${destination}" is not a same-origin URL; no redirect will run.`)

			return
		}

		// Sending the visitor to the page they are already on would reload it forever.
		if (currentPath === normalizePath(target.pathname)) {
			return
		}

		if (!window.gbpe?.auth) {
			this._evaluateWhenGBPEArrives()

			return
		}

		if (window.gbpe.auth.currentAuthState !== 1 || !window.gbpe.user) {
			return
		}

		const generation = authGenerationMap.get(this) ?? 0

		let rewards: GBPERewardsResponse | null

		try {
			rewards = await window.gbpe.user.rewards
		} catch (error) {
			window.console.warn(`gmx-auth-redirect: failed to read rewards from GBPE: ${error}`)

			return
		}

		// Auth moved on while rewards loaded, so this balance describes a session that is
		// no longer the current one.  The run started for that newer session answers
		// instead.
		if ((authGenerationMap.get(this) ?? 0) !== generation) {
			return
		}

		// Another run may have redirected while this one waited.
		if (hasRedirectedMap.get(this)) {
			return
		}

		// A sign-out while rewards loaded means this answer belongs to a stale session.
		if (window.gbpe?.auth?.currentAuthState !== 1) {
			return
		}

		if (!hasRewardsBalance(rewards)) {
			return
		}

		hasRedirectedMap.set(this, true)

		// `replace`, not `assign`: leaving the guarded page in history means the back
		// button lands on it again and bounces the visitor straight back here.
		window.location.replace(target.href)
	}

	/**
	 * GBPE is injected by the primary nav well after this bundle has run, and it does not
	 * reliably announce the state it starts in — `gmx-account` reads auth directly once
	 * the script loads and subscribes to `gbpe:authStateUpdated` only for later changes.
	 * Waiting on that event alone would strand a member who was already signed in when
	 * the page loaded, so watch for GBPE turning up instead.
	 *
	 * Its absence is a real misconfiguration but also the normal state for the first
	 * pass, so the complaint waits until the deadline and is made only once.
	 */
	private _evaluateWhenGBPEArrives(): void {
		if (gbpeWatchStartedMap.get(this)) {
			return
		}

		gbpeWatchStartedMap.set(this, true)

		const deadline = Date.now() + GBPE_WAIT_MS
		const timer = window.setInterval(() => {
			if (window.gbpe?.auth) {
				window.clearInterval(timer)
				void this.evaluate()

				return
			}

			if (Date.now() >= deadline) {
				window.clearInterval(timer)
				window.console.warn('gmx-auth-redirect: GBPE never initialised, so auth cannot be checked. Is gmx-account (primary nav) on this page?')
			}
		}, GBPE_POLL_MS)
	}
}

const authRedirectSingleton = Object.freeze(new GMXAuthRedirectSingleton())
export default authRedirectSingleton
